import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Brain, Lightbulb, Wrench, Search, Scale, Sparkles, 
  Puzzle, BookOpen, TrendingUp, TrendingDown, Minus,
  RefreshCw, Target, Award, BarChart2, Zap, ChevronRight,
  CheckCircle2, AlertCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping for skills
const skillIcons = {
  brain: Brain,
  lightbulb: Lightbulb,
  wrench: Wrench,
  search: Search,
  scale: Scale,
  sparkles: Sparkles,
  puzzle: Puzzle,
  book: BookOpen
};

export default function SkillsDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('faheem_token');

  useEffect(() => {
    if (token) {
      loadSkills();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadSkills = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/skills-dashboard/analyze?access_token=${token}`
      );
      if (response.data) {
        setDashboard(response.data);
      }
    } catch (error) {
      console.error('Failed to load skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'Expert':
        return 'bg-purple-600 text-white';
      case 'Advanced':
        return 'bg-blue-600 text-white';
      case 'Proficient':
        return 'bg-green-600 text-white';
      case 'Developing':
        return 'bg-yellow-600 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="skills-dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-500">Analyzing your skills...</p>
        </div>
      </div>
    );
  }

  if (!dashboard || dashboard.total_questions_analyzed === 0) {
    return (
      <Card className="bg-white border-slate-200" data-testid="skills-dashboard-empty">
        <CardContent className="py-16 text-center">
          <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Skills Data Yet</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Start taking quizzes to see your skill development based on Bloom's Taxonomy.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="skills-dashboard-container">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-0 shadow-xl overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Target className="w-7 h-7 text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">Skills Dashboard</h2>
                <p className="text-slate-400">Thinking Skills Insights</p>
              </div>
            </div>
            <Button 
              onClick={loadSkills} 
              variant="outline" 
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="refresh-skills-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6" data-testid="skills-quick-stats">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <Award className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-lg font-bold text-white">{dashboard.overall_skill_level}</p>
              <p className="text-xs text-slate-400">Overall Level</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <BarChart2 className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-lg font-bold text-white">{dashboard.total_questions_analyzed}</p>
              <p className="text-xs text-slate-400">Questions Analyzed</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <CheckCircle2 className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-lg font-bold text-white truncate" title={dashboard.strongest_skill}>
                {dashboard.strongest_skill || 'N/A'}
              </p>
              <p className="text-xs text-slate-400">Strongest</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <Zap className="w-6 h-6 text-orange-400 mx-auto mb-2" />
              <p className="text-lg font-bold text-white">{dashboard.skill_balance}%</p>
              <p className="text-xs text-slate-400">Skill Balance</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Skills Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {dashboard.skills.map((skill) => {
          const IconComponent = skillIcons[skill.icon] || Brain;
          return (
            <Card 
              key={skill.skill_id} 
              className="bg-white border-slate-200 hover:shadow-lg transition-all"
              data-testid={`skill-card-${skill.skill_id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${skill.color}20` }}
                  >
                    <IconComponent 
                      className="w-6 h-6" 
                      style={{ color: skill.color }}
                    />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-2">
                      {getTrendIcon(skill.trend)}
                      <Badge className={getLevelBadgeColor(skill.level)}>
                        {skill.level}
                      </Badge>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {skill.bloom_level} Order
                    </Badge>
                  </div>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-1">{skill.name}</h3>
                <p className="text-xs text-slate-500 mb-4 line-clamp-2">{skill.description}</p>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Proficiency</span>
                      <span className="font-bold" style={{ color: skill.color }}>{skill.score}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${skill.score}%`,
                          backgroundColor: skill.color 
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{skill.questions_correct}/{skill.questions_attempted} correct</span>
                    {skill.recent_score !== skill.score && (
                      <span className={skill.recent_score > skill.score ? 'text-green-600' : 'text-red-600'}>
                        Recent: {skill.recent_score}%
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Skill Radar / Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Skill Distribution */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <BarChart2 className="w-4 h-4 text-indigo-600" />
              </div>
              Skill Distribution
            </CardTitle>
            <CardDescription>Your performance across cognitive skills</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboard.skills.map((skill) => (
                <div key={skill.skill_id} className="flex items-center gap-3">
                  <div className="w-32 text-sm font-medium text-slate-700 truncate">
                    {skill.name}
                  </div>
                  <div className="flex-1">
                    <div className="h-6 bg-slate-100 rounded-full overflow-hidden relative">
                      <div 
                        className="h-full rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                        style={{ 
                          width: `${Math.max(skill.score, 8)}%`,
                          backgroundColor: skill.color 
                        }}
                      >
                        {skill.score >= 20 && (
                          <span className="text-xs font-bold text-white">{skill.score}%</span>
                        )}
                      </div>
                      {skill.score < 20 && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-600">
                          {skill.score}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <Lightbulb className="w-4 h-4 text-amber-600" />
              </div>
              Skill Development Tips
            </CardTitle>
            <CardDescription>Personalized recommendations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.recommendations.map((rec, idx) => (
                <div 
                  key={idx} 
                  className="flex items-start gap-3 p-3 bg-gradient-to-r from-slate-50 to-amber-50 rounded-xl border border-slate-100"
                >
                  <ChevronRight className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-slate-700">{rec}</p>
                </div>
              ))}
            </div>
            
            {/* Focus Area */}
            {dashboard.weakest_skill && (
              <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-100">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <h4 className="font-semibold text-red-800">Focus Area</h4>
                </div>
                <p className="text-sm text-red-700">
                  Your <strong>{dashboard.weakest_skill}</strong> skills need the most attention. 
                  Try practicing questions that involve this cognitive level.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Thinking Skills Overview - Dynamic from actual data */}
      <Card className="bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            Thinking Skills Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            FaheemGPT analyzes your quiz answers to track your development across these cognitive skills, 
            organized by Bloom's Taxonomy levels:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {dashboard.skills.map((skill) => {
              const IconComponent = skillIcons[skill.icon] || Brain;
              return (
                <div 
                  key={skill.skill_id} 
                  className="bg-white rounded-lg p-3 border border-slate-100 flex items-start gap-3"
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${skill.color}20` }}
                  >
                    <IconComponent className="w-4 h-4" style={{ color: skill.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 text-sm">{skill.name}</p>
                    <p className="text-xs text-slate-500 truncate">{skill.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">{skill.bloom_level}</Badge>
                      <span className="text-xs font-semibold" style={{ color: skill.color }}>
                        {skill.score}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
